import { useState, useEffect } from 'react';
import { STAGING_LOAN } from '../../config/env';
import { Topbar, Aside, Main, ApplicationDetails, Documents, PaymentDetails, Loader, Repayment, LoanProgressBar, Error } from 'components';
import { useUser } from 'hooks';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { show } from 'providers/toastSlice';
import { fetchRequests } from 'https/requests';
import { Tab } from '@headlessui/react'

function Index() {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { accessToken } = useUser();
    const location = useLocation();
    const { pathname } = location;
    const path = pathname.split("/");
    const state = path[2];
    const loanType = path[3];
    const loanId = path[4];

    const [isLoading, setIsLoading] = useState(true);
    const [loan, setLoan] = useState(null);
    const [errMsg, setErrMsg] = useState(null);

    function classNames(...classes) {
        return classes.filter(Boolean).join(' ')
    }

    const stageLabels = {
        lo:                   'Under review by Loan Officer',
        pm_review:            'Under review by Portfolio Manager',
        hoc:                  'Under review by Head of Credit',
        crm:                  'Under review by Credit Risk Manager',
        hor:                  'Under review by Head of Risk',
        pm_disburse:          'Pending disbursement approval',
        pendingDisbursement:  'Awaiting disbursement',
        pendingFacilityLetter:'Awaiting facility letter signature',
    };

    const stageLabel = loan?.state === 'initial' || loan?.state === 'approved'
        ? stageLabels[loan?.adminStage] ?? null
        : null;

    useEffect(() => {
        const fetchLoans = async () => {
            setIsLoading(true);

            const url = `${STAGING_LOAN}/preview`;

            const headers = {
                "Authorization" : `${accessToken}`,
                "Content-Type": "application/json"
            }

            const params = {
                loanId : loanId,
                loanType : loanType,
                state : state
            }

            try {

                let res = await fetchRequests(url, "get", headers, params, {});

                if(res?.code === 401){
                    navigate("/login");
                    dispatch(show({
                        state : true,
                        message : `Your session has expired, please login to continue.`,
                        type : "token"
                    }))
                    return;
                }

                if(res?.err){
                    setErrMsg(res?.err);
                    dispatch(show({
                        state : true,
                        message : `${res?.err}`,
                        type : "error"
                    }))
                    return;
                }

                setLoan(res)

            } catch (error) {
                dispatch(show({
                    state : true,
                    message : `${error}`,
                    type : "error"
                }))
            } finally {
                setIsLoading(false);
            }
        }
        
        fetchLoans();

    }, [dispatch, navigate, loanType, loanId, state, accessToken])

    if(isLoading){
        return (
            <Loader />
        )
    }

    if(!loan){
        return (
            <Error code={"500"} message={errMsg} />
        )
    }

    return (
        <main>
            <Topbar />
            
            <div className='pt-14 w-full flex overflow-hidden bg-light-bg dark:bg-dark-page'>
                <Aside />

                <Main>

                    <div className='display_card'>

                        {stageLabel && (
                            <div className='inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-blue-50/70 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-medium ring-1 ring-inset ring-blue-100 dark:ring-blue-900/40'>
                                <span className='w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse inline-block'></span>
                                {stageLabel}
                            </div>
                        )}

                        {loan?.state !== "rejected" && (
                            <LoanProgressBar
                                current={(loan?.state === "approved" && loan?.facilityLetterUploaded) ? "pendingDisbursement" : loan?.state}
                                type={loan?.type}
                            />
                        )}

                        {/* show this when loan is in active state */}
                        {loan?.state === "active" && (
                            <Repayment loan={loan} />
                        )}
                        {/* show this when loan is in active state */}

                        <Tab.Group>
                            <Tab.List className={`flex items-center gap-6 border-b border-gray-200 dark:border-white/[.1] ${loan?.state === 'active' ? 'mt-10' : 'mt-6'}`}>
                                <Tab className={({ selected }) => classNames('relative -mb-px py-3 text-sm font-medium transition-colors focus:outline-none', selected ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white' : 'text-gray-500 dark:text-white/[.5] hover:text-gray-700 dark:hover:text-white/[.8]')}>
                                    Application Details
                                </Tab>
                                <Tab className={({ selected }) => classNames('relative -mb-px py-3 text-sm font-medium transition-colors focus:outline-none', selected ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white' : 'text-gray-500 dark:text-white/[.5] hover:text-gray-700 dark:hover:text-white/[.8]')}>
                                    Loan Documents
                                </Tab>
                                <Tab className={({ selected }) => classNames('relative -mb-px py-3 text-sm font-medium transition-colors focus:outline-none', selected ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white' : 'text-gray-500 dark:text-white/[.5] hover:text-gray-700 dark:hover:text-white/[.8]')}>
                                    Payment History
                                </Tab>
                            </Tab.List>

                            <Tab.Panels className="mt-6">
                                <Tab.Panel className={classNames('focus:outline-none')}>
                                    <ApplicationDetails loan={loan} />
                                </Tab.Panel>

                                <Tab.Panel className={classNames('focus:outline-none')}>
                                    <Documents loan={loan} />
                                </Tab.Panel>

                                <Tab.Panel className={classNames('focus:outline-none')}>
                                    <PaymentDetails loan={loan} />
                                </Tab.Panel>
                            </Tab.Panels>
                        </Tab.Group>
                    </div>

                </Main>

            </div>
        </main>
    )
}

export default Index